import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";

import { useNotify } from "react-admin";

import { QrBarcodeScanner } from "../../../components/common/QrBarcodeScanner/QrBarcodeScanner";
import { ConditionalConfirm } from "../../../components/common/ConditionalConfirm";
import { beep } from "./../../../utils/utils";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  closeButton: {
    cursor: "pointer",
  },
}));

export const ApplySealsDialog = ({
  onClose,
  saveSeal,
  deActivateSeal,
  reActivateSeal,
  seal,
  inboundShipment,
  dispatch,
  loading,
}) => {
  const createdBy = window.appUser.fullName;
  const seals = inboundShipment?.seals?.items || [];
  const [barcode, setBarcode] = useState("");
  const [sealError, setSealError] = useState("");
  const [alreadyAddedDialogOpen, setAlreadyAddedDialogOpen] = useState(false);
  const [isScanned, setIsScanned] = useState(false);
  const styles = useStyles();
  const [isScannerOpen, setIsScannerOpen] = useState(true);
  const notify = useNotify();
  const ref = useRef();
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      ref.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, []);

  const save = async () => {
    if (loading || saveInFlightRef.current) return;
    if (!barcode) {
      setSealError("Required");
      return;
    }

    if (seals.some((s) => s.barcode === barcode)) {
      setAlreadyAddedDialogOpen(true);
      return;
    }

    saveInFlightRef.current = true;
    let updatedInbound = null;
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      updatedInbound = await deActivateSeal({
        notify,
        dispatch,
        inboundShipment,
        createdBy,
        barcode: seal.barcode,
        sealId: seal.id,
      });
      await saveSeal({
        notify,
        inboundShipment: updatedInbound,
        barcode,
        note: seal.description,
        createdBy,
        dispatch,
        scanned: isScanned,
      });
      onClose();
    } catch (error) {
      if (updatedInbound?.id && reActivateSeal) {
        await reActivateSeal({
          notify,
          dispatch,
          inboundShipment: updatedInbound,
          createdBy,
          barcode: seal.barcode,
          sealId: seal.id,
        });
      }
    } finally {
      saveInFlightRef.current = false;
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleScanSuccess = (v) => {
    if (v !== ref.current?.value) {
      setBarcode(v);
      setIsScanned(true);
      beep(100, 520, 200);
    }
  };

  const handleAlreadyAddedDialogClose = () => {
    setAlreadyAddedDialogOpen(false);
    setIsScanned(false);
    setBarcode("");
  };

  const handleBarcodeInputChange = (e) => {
    if (e.target.value) {
      setSealError("");
    } else {
      setSealError("Required");
    }
    setBarcode(e.target.value);
    setIsScanned(false);
  };

  return (
    <>
      <Dialog open={true} fullWidth={true}>
        <Box
          component="span"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          m={2}
        >
          <Typography variant="h6">Replace Seal</Typography>
          <CancelOutlinedIcon
            className={styles.closeButton}
            fontSize="large"
            onClick={onClose}
          />
        </Box>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {isScannerOpen ? (
                <QrBarcodeScanner onScanSuccess={handleScanSuccess} />
              ) : (
                <Box display="flex" justifyContent="center" mb={2}>
                  <Button
                    onClick={() => setIsScannerOpen(true)}
                    color="primary"
                    variant="outlined"
                    margin="dense"
                  >
                    Rescan
                  </Button>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Barcode"
                variant="outlined"
                fullWidth
                margin="dense"
                value={barcode}
                onChange={handleBarcodeInputChange}
                error={!!sealError}
                helperText={sealError}
                disabled={isScanned}
                inputRef={ref}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (loading || saveInFlightRef.current) return;
                    save();
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {!loading && <Button onClick={onClose}>Close</Button>}
          <Button color="primary" onClick={save} disabled={loading}>
            {loading ? <CircularProgress color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConditionalConfirm
        isOpen={alreadyAddedDialogOpen}
        loading={loading}
        title="Scan Confirmation"
        content="This seal is already added in the inbound shipment. Please scan again."
        onClose={handleAlreadyAddedDialogClose}
        cancel="Close"
      />
    </>
  );
};
